import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Shield, Clock, Banknote } from "lucide-react";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";

const HomePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 
                className="text-4xl md:text-5xl font-bold text-primary tracking-tight leading-tight"
                data-testid="hero-title"
              >
                Bienvenue sur ECOPAY
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Vérifiez et transférez vos fonds de financement en toute simplicité
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                ECOPAY est la plateforme sécurisée qui simplifie la gestion de vos fonds de financement d'entreprise. 
                Accédez rapidement à vos fonds, vérifiez votre solde disponible et effectuez des transferts en quelques clics.
              </p>
              <Link 
                to="/connexion" 
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-md hover:bg-primary/90 transition-all shadow-md hover:shadow-lg font-medium"
                data-testid="hero-cta-btn"
              >
                Accéder à mon compte
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1651331189447-704ac3643c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Njk3MTYzMTh8MA&ixlib=rb-4.1.0&q=85"
                alt="Bureau moderne"
                className="rounded-xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="fonctionnement" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-slate-600">
              Un processus simple et sécurisé en 4 étapes
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all">
              <div className="bg-primary/10 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Connexion</h3>
              <p className="text-slate-600">
                Connectez-vous avec votre code d'identification unique fourni par votre conseiller.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all">
              <div className="bg-primary/10 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Vérification</h3>
              <p className="text-slate-600">
                Consultez le solde de vos fonds de financement disponibles sur votre tableau de bord.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all">
              <div className="bg-primary/10 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Authentification</h3>
              <p className="text-slate-600">
                Authentifiez-vous auprès de votre banque pour valider l'éligibilité de votre compte.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all">
              <div className="bg-primary/10 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Transfert</h3>
              <p className="text-slate-600">
                Validez le transfert vers votre compte bancaire en toute sécurité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="avantages" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Pourquoi choisir ECOPAY ?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <Shield className="h-12 w-12 text-secondary mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-primary">Sécurité maximale</h3>
              <p className="text-slate-600">
                Toutes vos transactions sont protégées par un cryptage SSL de niveau bancaire.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <Clock className="h-12 w-12 text-secondary mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-primary">Rapide et simple</h3>
              <p className="text-slate-600">
                Accédez à vos fonds en quelques minutes grâce à notre processus optimisé.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <Banknote className="h-12 w-12 text-secondary mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-primary">Transparent</h3>
              <p className="text-slate-600">
                Visualisez en temps réel le montant exact de vos fonds disponibles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBmaW5hbmNlfGVufDB8fHx8MTc2OTcxNjMxNnww&ixlib=rb-4.1.0&q=85"
                alt="Confiance professionnelle"
                className="rounded-xl shadow-xl w-full h-auto"
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl font-semibold text-primary">
                Une plateforme de confiance
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                  <p className="text-slate-600">
                    Conformité totale avec les réglementations bancaires et financières
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                  <p className="text-slate-600">
                    Support dédié pour vous accompagner à chaque étape
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                  <p className="text-slate-600">
                    Interface intuitive conçue pour les entrepreneurs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
